// Matrices tomadas del plan de referencia de la Empresa A (PASO0_DISENO.md §3.3.4 y §3.3.5)
// y lista de condiciones del punto de encuentro aprobada en PE-8 (matriz, fila 7.22).

import { doc, unirFuentes } from './pe-tipos';

// ── Amenaza × vulnerabilidad 4×4 (plan p. 30; PE-15) ─────────────────────────

export type Nivel4 = 'bajo' | 'medio' | 'alto' | 'muy_alto';

// Etiqueta del nivel con la concordancia del sustantivo: "riesgo medio" (m) y "amenaza media",
// "vulnerabilidad media" (f) (PE-A3c).
const ETIQUETA_NIVEL4 = {
  m: { bajo: 'bajo', medio: 'medio', alto: 'alto', muy_alto: 'muy alto' },
  f: { bajo: 'baja', medio: 'media', alto: 'alta', muy_alto: 'muy alta' },
} as const;
export const etiquetaNivel = (n: Nivel4, genero: 'm' | 'f'): string => ETIQUETA_NIVEL4[genero][n];

// Filas: amenaza; columnas: vulnerabilidad (bajo, medio, alto, muy_alto).
export const MATRIZ_AMENAZA_VULNERABILIDAD: Record<Nivel4, Record<Nivel4, Nivel4>> = {
  muy_alto: { bajo: 'alto', medio: 'alto', alto: 'muy_alto', muy_alto: 'muy_alto' },
  alto: { bajo: 'medio', medio: 'medio', alto: 'alto', muy_alto: 'muy_alto' },
  medio: { bajo: 'bajo', medio: 'medio', alto: 'medio', muy_alto: 'alto' },
  bajo: { bajo: 'bajo', medio: 'bajo', alto: 'medio', muy_alto: 'alto' },
};

export const FUENTE_MATRIZ_AMENAZAS = {
  norma: 'Matriz amenaza × vulnerabilidad del plan de referencia',
  fuente_doc: unirFuentes(doc.paso0('§3.3.4'), doc.matriz(145)),
};

// ── Probabilidad × consecuencia del punto de encuentro (plan p. 46) ──────────

export type Probabilidad = 'B' | 'M' | 'A';
export type Consecuencia = 'LD' | 'D' | 'ED';
export type RiesgoPC = 'T' | 'TO' | 'MO' | 'I' | 'IN';

export const MATRIZ_PROBABILIDAD_CONSECUENCIA: Record<Probabilidad, Record<Consecuencia, RiesgoPC>> = {
  B: { LD: 'T', D: 'TO', ED: 'MO' },
  M: { LD: 'TO', D: 'MO', ED: 'I' },
  A: { LD: 'MO', D: 'I', ED: 'IN' },
};

export const ETIQUETA_RIESGO_PC: Record<RiesgoPC, string> = {
  T: 'trivial', TO: 'tolerable', MO: 'moderado', I: 'importante', IN: 'intolerable',
};

export const ORDEN_RIESGO_PC: RiesgoPC[] = ['T', 'TO', 'MO', 'I', 'IN'];

// Los 6 peligros que evalúa el plan en el punto de encuentro.
export const PELIGROS_PUNTO_ENCUENTRO = [
  { clave: 'caida_objetos', etiqueta: 'Objetos peligrosos o edificaciones que pueden caer sobre las personas' },
  { clave: 'desniveles', etiqueta: 'Desniveles u obstáculos que pueden provocar caídas' },
  { clave: 'transito_vehiculos', etiqueta: 'Tránsito de vehículos que puede provocar atropellamientos' },
  { clave: 'inflamables', etiqueta: 'Proximidad de sustancias inflamables (incendios o explosiones)' },
  { clave: 'lineas_electricas', etiqueta: 'Líneas eléctricas o equipos energizados' },
  { clave: 'piso_inestable', etiqueta: 'Piso inestable que puede provocar caídas a otro nivel' },
] as const;

export const FUENTE_MATRIZ_PC = {
  norma: 'Matriz probabilidad × consecuencia del plan de referencia',
  fuente_doc: doc.paso0('§3.3.5'),
};

// ── Lista de condiciones del punto de encuentro (PE-8) ───────────────────────

// Todas son eliminatorias (PE-24, Kevin 26/10/2026): si una no se cumple, el punto no sirve.
export const CONDICIONES_PUNTO_ENCUENTRO = [
  { clave: 'aire_libre', etiqueta: 'Al aire libre', eliminatoria: true },
  { clave: 'accesible', etiqueta: 'Accesible', eliminatoria: true },
  { clave: 'sin_transito_vehiculos', etiqueta: 'Sin tránsito vehicular', eliminatoria: true },
  { clave: 'sin_cables_arboles', etiqueta: 'Sin cables ni árboles altos encima', eliminatoria: true },
  { clave: 'fuera_otros_peligros', etiqueta: 'Fuera de otros peligros', eliminatoria: true },
] as const;

export const FUENTE_CONDICIONES = {
  norma: 'Criterio técnico del responsable',
  fuente_doc: doc.matriz(142),
};
